/**
 * aces2 — ACES 2.0-style tone mapping.
 *
 * Full ACES 2.0 (published February 2024) is a ~20-operator pipeline with
 * a CIECAM16 forward/inverse pass. That's not shippable for real-time web.
 * We ship a **norm-based ratio-preserving approximation** — the minimum
 * viable subset that captures the "hue preservation" win of 2.0 over 1.x.
 *
 * Algorithm:
 *   1. Compute a perceptual norm of the RGB triplet (weighted max + luma).
 *   2. Apply a Michaelis-Menten tone scale to the norm, parameterized
 *      by peak output luminance.
 *   3. Scale RGB by `norm_mapped / norm_input` (hue-preserving gain).
 *   4. Apply mild chroma-compress toward the norm near highlights.
 *
 * Parameterization by `peakNits`:
 *   - 100 nits  → standard SDR (sRGB / Display-P3 displays)
 *   - 1000 nits → typical HDR10
 *   - 4000 nits → HDR10+ / Dolby Vision premium
 *
 * References:
 *   - ACES 2.0 dev: https://github.com/ampas/aces-core
 *   - OCIO 2.5 ACES 2.0 config: https://opencolorio.readthedocs.io/
 *   - "ACES 2.0 in Game Engines" — acescentral.com forum
 */

import {
  Fn, float, vec3, max, min, pow, mix, smoothstep,
} from 'three/tsl';

/**
 * CPU reference — Michaelis-Menten tone scale.
 *
 *   m = 1.1                           (max output — slightly above 1)
 *   n = 0.18 * (peakNits / 100)       (18% gray anchor scaled by peak)
 *   c = 0.4                           (contrast)
 *   out = m * x^c / (x^c + n^c)
 *
 * At x = 0 → 0. At x = ∞ → m. Monotonic increasing.
 */
export function aces2ToneScale(x: number, peakNits = 100): number {
  if (x <= 0) return 0;
  const m = 1.1;
  const n = 0.18 * (peakNits / 100);
  const c = 0.4;
  const num = m * Math.pow(x, c);
  const den = Math.pow(x, c) + Math.pow(n, c);
  return num / den;
}

/**
 * "Perceptual norm" used by the ratio-preserving step — a weighted blend
 * of `max(r, g, b)` and `luma(r, g, b)`. Captures both peak magnitude (for
 * highlight shaping) and luminance (for mid-tone placement). This replaces
 * a full JMh perceptual conversion from the ACES 2.0 reference.
 */
export function aces2Norm(r: number, g: number, b: number): number {
  const peak = Math.max(r, Math.max(g, b));
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return peak * 0.7 + luma * 0.3;
}

/**
 * Full ACES 2.0 ratio-preserving tone map, CPU reference.
 *
 *   1. n = norm(rgb)
 *   2. n' = toneScale(n, peakNits)
 *   3. gain = n' / n
 *   4. rgb' = rgb * gain
 *   5. chromaCompress: mix(rgb', vec3(n'), clamp(n', 0, 1)² * 0.15)
 *
 * The 0.15 chroma-compress factor is the empirical "gentle path-to-white"
 * used by community ACES 2.0 ports. Higher values feel more like ACES 1.0
 * (desaturated highlights); lower values preserve more color at the risk
 * of "clipped primaries" appearance in very bright regions.
 */
export function aces2Tonemap(
  r: number, g: number, b: number,
  peakNits = 100,
  chromaCompressAmount = 0.15,
): { r: number; g: number; b: number } {
  if (r <= 0 && g <= 0 && b <= 0) return { r: 0, g: 0, b: 0 };

  const n = aces2Norm(r, g, b);
  if (n <= 0) return { r: 0, g: 0, b: 0 };

  const nMapped = aces2ToneScale(n, peakNits);
  const gain = nMapped / n;

  let rOut = r * gain;
  let gOut = g * gain;
  let bOut = b * gain;

  // Chroma compress: mix toward neutral (nMapped) near highlights.
  const nClamp = Math.min(1, Math.max(0, nMapped));
  const t = nClamp * nClamp * chromaCompressAmount;
  rOut = rOut * (1 - t) + nMapped * t;
  gOut = gOut * (1 - t) + nMapped * t;
  bOut = bOut * (1 - t) + nMapped * t;

  return { r: rOut, g: gOut, b: bOut };
}

// ─── TSL node factory ──────────────────────────────────────────────────────

/**
 * Build a TSL tone-map function that evaluates ACES 2.0 on an RGB node.
 *
 *   const tonemap = createAces2ToneMapNode(peakNitsUniform);
 *   const outputNode = tonemap(sceneColorNode);
 *
 * `peakNitsUniform` must be a TSL uniform node; push peak changes via its
 * `.value = ...` setter.
 */
export function createAces2ToneMapNode(
  peakNitsUniform: unknown,
  chromaCompressAmount = 0.15,
): (rgb: unknown) => unknown {
  // The Michaelis-Menten tone scale, wrapped in a TSL Fn so it can be
  // reused across channels if ever needed.
  const toneScale = Fn(([x, peak]: [ReturnType<typeof float>, ReturnType<typeof float>]) => {
    const m = float(1.1);
    const n = float(0.18).mul(peak).div(100);
    const c = float(0.4);
    const xc = pow(x, c);
    const nc = pow(n, c);
    return m.mul(xc).div(xc.add(nc));
  }) as unknown as (args: [unknown, unknown]) => unknown;

  return (rgbNode: unknown): unknown => {
    const rgb = rgbNode as {
      r: ReturnType<typeof float>;
      g: ReturnType<typeof float>;
      b: ReturnType<typeof float>;
      dot: (v: unknown) => ReturnType<typeof float>;
      mul: (v: unknown) => ReturnType<typeof vec3>;
    };
    const peakC = max(max(rgb.r, rgb.g), rgb.b);
    const luma = rgb.dot(vec3(0.2126, 0.7152, 0.0722));
    const n = peakC.mul(0.7).add(luma.mul(0.3));
    const nSafe = max(n, float(1e-5));
    const nMapped = toneScale([nSafe, peakNitsUniform]) as ReturnType<typeof float>;

    const gain = nMapped.div(nSafe);
    const scaled = rgb.mul(gain) as ReturnType<typeof vec3>;

    const nClamp = min(max(nMapped, float(0)), float(1));
    const t = nClamp.mul(nClamp).mul(chromaCompressAmount);
    const neutral = vec3(nMapped, nMapped, nMapped);
    return mix(scaled, neutral, t);
  };
}

// ─── The published ACES 2.0 tone scale (2026-09-25, the lighting A/B) ──────
//
// The Michaelis-Menten scale above has NO TOE (x^0.4 has an infinite slope at
// 0): a 0.002 black comes out at 107/255 sRGB and an 18 % card at 196/255 — a
// milky picture (measured on the CPU for the Atlas AgX-vs-ACES2 comparison).
// This is Daniele Siragusano's tone scale as the ACES 2.0 output transform
// ships it (aces-core, "Tonescale" block): a power-compressed hyperbola with a
// quadratic toe, 18 % scene grey → 10 nits of a 100-nit display (0.10), a
// real black and a soft shoulder to the peak. Parameters are the published
// ones (g 1.15, c_d 10.013 nits, w_g 0.14, t_1 0.04, r_hit 128..896).

/** The derived constants of the ACES 2.0 tone scale for a peak luminance. */
export interface Aces2ToneParams { peakNits: number; m2: number; s2: number; g: number; t1: number }

export function aces2DanieleParams(peakNits = 100): Aces2ToneParams {
  const n = Math.max(1, peakNits), nr = 100, g = 1.15, c = 0.18, cd = 10.013, wg = 0.14, t1 = 0.04;
  const rHit = 128 + (896 - 128) * (Math.log(n / nr) / Math.log(10000 / 100));
  const m0 = n / nr;
  const m1 = 0.5 * (m0 + Math.sqrt(m0 * (m0 + 4 * t1)));
  const u = Math.pow((rHit / m1) / ((rHit / m1) + 1), g);
  const m = m1 / u;
  const wi = Math.log2(n / 100);
  const ct = (cd / nr) * (1 + wi * wg);
  const gip = 0.5 * (ct + Math.sqrt(ct * (ct + 4 * t1)));
  const q = Math.pow(gip / m, 1 / g);
  const gipp2 = -(m1 * q) / (q - 1);
  const w2 = c / gipp2;
  const s2 = w2 * m1;
  const u2 = Math.pow((rHit / m1) / ((rHit / m1) + w2), g);
  const m2 = m1 / u2;
  return { peakNits: n, m2, s2, g, t1 };
}

/** The SDR constants (100 nits). */
export const ACES2_SDR = aces2DanieleParams(100);

/** ACES 2.0 tone scale, CPU reference: scene-linear → display-relative (1 = the peak). */
export function aces2DanieleToneScale(x: number, p: Aces2ToneParams = ACES2_SDR): number {
  const xx = Math.max(0, x);
  const f = p.m2 * Math.pow(xx / (xx + p.s2), p.g);
  const h = Math.max(0, (f * f) / (f + p.t1));
  return h * (100 / p.peakNits);
}

/**
 * The display transform, CPU reference: `preGain` scales the scene first (the
 * Atlas A/B grey-matches AgX with it — ACES puts an 18 % card at 10 % of the
 * display, AgX at ~22 %); the tone scale runs on max(r, g, b) and the triplet
 * is scaled by the ratio (hue kept); `pathToWhite` desaturates what reaches the
 * top of the range so a sun disc goes white instead of clipping to a primary.
 */
export function aces2DisplayTonemap(
  r: number, g: number, b: number, preGain = 1, pathToWhite = 0.6, p: Aces2ToneParams = ACES2_SDR,
): { r: number; g: number; b: number } {
  // THE FINITE GUARD (shader sweep 2026-10-06): an Inf pixel (a sun disc past fp16) made n/(n + s2) = Inf/Inf = NaN —
  // the clamp to fp16's largest finite value keeps it the peak's white (the shader's twin clamps the same).
  const F = ACES2_FINITE_MAX;
  const R = Math.max(0, Math.min(r, F) * preGain), G = Math.max(0, Math.min(g, F) * preGain), B = Math.max(0, Math.min(b, F) * preGain);
  const n = Math.max(R, G, B);
  if (n <= 0) return { r: 0, g: 0, b: 0 };
  const h = aces2DanieleToneScale(n, p);
  const k = h / n;
  const e = Math.min(1, Math.max(0, (h - 0.55) / 0.5));
  const t = e * e * (3 - 2 * e) * pathToWhite;
  const c = (v: number) => Math.min(1, v * k * (1 - t) + h * t);
  return { r: c(R), g: c(G), b: c(B) };
}

/** The largest finite fp16 — the input clamp of the display transform (an Inf HDR pixel must come out white, not NaN). */
export const ACES2_FINITE_MAX = 65504;

/** The TSL twin of `aces2DisplayTonemap` (the constants baked for a peak; `preGain` may be a uniform node). */
export function createAces2DisplayNode(opts: { peakNits?: number; preGain?: unknown; pathToWhite?: number } = {}): (rgb: unknown) => unknown {
  const p = aces2DanieleParams(opts.peakNits ?? 100);
  const ptw = opts.pathToWhite ?? 0.6;
  return (rgbNode: unknown): unknown => {
    type V = ReturnType<typeof vec3>;
    // WHY the upper clamp (shader sweep 2026-10-06): an Inf scene pixel (a sun disc past fp16 — the Purkinje stage
    // passes its input through untouched at k = 0) gave nSafe/(nSafe + s2) = Inf/Inf = NaN and a NaN on screen.
    const rgb0 = min(max(vec3(rgbNode as never), vec3(0, 0, 0)), vec3(ACES2_FINITE_MAX, ACES2_FINITE_MAX, ACES2_FINITE_MAX)) as unknown as V;
    const rgb = (opts.preGain !== undefined ? rgb0.mul(opts.preGain as never) : rgb0) as unknown as V;
    const n = max(max(rgb.x, rgb.y), rgb.z);
    const nSafe = max(n, float(1e-6));
    const f = float(p.m2).mul(pow(nSafe.div(nSafe.add(p.s2)), float(p.g)));
    const h = max(f.mul(f).div(f.add(p.t1)), float(0)).mul(100 / p.peakNits);
    const k = h.div(nSafe);
    const t = smoothstep(float(0.55), float(1.05), h).mul(ptw);
    return min(mix(rgb.mul(k), vec3(h, h, h), t), vec3(1, 1, 1));
  };
}
